/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
/* eslint-disable @ngrx/avoid-dispatching-multiple-actions-sequentially */
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Store } from '@ngrx/store';
import { LotNumbers, PanelInfoResponse } from '../../models/panel.model';
import { CalLot } from '../../models/cal-lot.model';
import { QcLot } from '../../models/qc-lot.model';
import { FileMetaData, ReportContentItem, ReportContentItemResponse, RunSampleLot, Sample } from '../../models/run.model';
import { Panel } from '../../models/assays.model';
import {  Study, StudyListRequest, StudyListResponse, StudyLoadRequest, StudyListItem, DraftStudyData, LoadDraftStudyResponse, DraftLotEntry, dedupeLotVersions } from '../../models/study.model';
import { AppUtilityService } from '../../services/app-utility.service';
import { apiPaths } from '../../../app/core/constants/api.const';
import {  Observer, Subject, take } from 'rxjs';
import { selectAppState } from '../../../app/state/selectors';
import { MatSelectChange } from '@angular/material/select';
import { MatDialogRef } from '@angular/material/dialog';
import { loadCalLot, loadQcLot, setLoadStudyError, setPanel, setProcessingStatus, setStudy } from '../../../app/state/actions';
import { PhaseTypeOptions } from '../../models/run.model';
import { alignRunSampleLots, applySampleTypeOverrides } from '../../utils/run-info.utils';
import { getLatestAuditTrailComment } from '../../utils/audit-comment.util';
import { CalAdjustmentMode, isCalAdjustFinal, toApiWorkflow } from '../../utils/run-type.util';
import { StudyLogbookService } from '../../services/study-logbook.service';
import { StudyLinksService } from '../../services/study-links.service';


interface UserInfo {
  first_name: string;
  last_name: string;
  email: string;
}

interface UsersResponse {
  users: (UserInfo & { is_active: boolean })[];
}

/**
 * LoadStudyDialogComponent
 * This component manages the "Load Study" dialog where users can select a study from a list based on
 * a panel and phase. The studies are fetched using a service, and users can confirm their selection
 * to load the selected study.
 */
@Component({
  selector: 'app-load-study-dialog',
  templateUrl: './load-study-dialog.component.html',
  styleUrl: './load-study-dialog.component.scss'
})
export class LoadStudyDialogComponent implements OnInit, OnDestroy {

  appState$ = this.store.select(selectAppState);
  destroy$ = new Subject<boolean>();
  panels: LotNumbers[] = [];
  phaseTypes = PhaseTypeOptions;
  private readonly internalQcBeadRegions = new Set<number>([34, 98]);
  selectedPanelName!: string;
  selectedPhaseType!: string;
  users: UserInfo[] = [];
  selectedUserName: string | undefined;
  studies: string[] = [];// = mockData.mockPanelPhaseStudyData;
  selectedStudies!: StudyListItem[];
  filteredStudies: StudyListItem[] = [];
  studyFilterText = '';
  userColWidth = '8rem';
  selectedStudy: StudyListItem[] | undefined;
  errorMessage = '';
  isProcessing = false;
  isLoadStudyConfirmed = false;
  /**
   * 
   * @param store Injecting store for state management.
   * @param appUtilityService Service for API calls and utility functions.
   */
  constructor(
    private store: Store,
    private appUtilityService: AppUtilityService,
    private dialogRef: MatDialogRef<LoadStudyDialogComponent>,
    private studyLogbookService: StudyLogbookService,
    private studyLinksService: StudyLinksService
  ) { }
  /** ngOnInit lifecycle hook for component initialization. */
  ngOnInit(): void {

    const postObserver = {
      next: (panelResponse: PanelInfoResponse) => {

        this.panels = [...(panelResponse.panels ?? [])].sort((a, b) => a.panelName.localeCompare(b.panelName));
      },
      error: (err: Error) => {
        this.panels = [];
        this.errorMessage = this.appUtilityService.getError(err).message;
        this.isProcessing = false;
      },
      complete: () => {
        this.isProcessing = false;
      }
    } as Observer<PanelInfoResponse>;

    this.appUtilityService.get<PanelInfoResponse>(this.appUtilityService.getAPI(apiPaths.panelInfo.panels))
      .pipe(take(1))
      .subscribe(postObserver);

    this.loadUsers();
  }

  loadUsers(): void {
    this.appUtilityService.get<UsersResponse>(this.appUtilityService.getAPI(apiPaths.users.getUserInfo))
      .pipe(take(1))
      .subscribe({
        next: response => {
          this.users = (response?.users ?? [])
            .filter(user => user.is_active)
            // Sorted on the displayed name, not the email the option is keyed by.
            .sort((a, b) =>
              `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
        },
        error: () => {
          this.users = [];
        }
      });
  }

  userChangeAction($event: MatSelectChange) {
    if (this.selectedPanelName && this.selectedPhaseType) {
      const studyListRequest = {
        panel_name: this.selectedPanelName,
        workflow: this.normalizeWorkflowForDisplay(this.selectedPhaseType),
        user_name: $event.value ?? undefined
      } as StudyListRequest;
      this.loadStudies(studyListRequest);
    }
  }
  /** Handler for changes in the selected panel. */
  panelChangeAction($event: MatSelectChange) {
    if ($event.value) {
      if (this.selectedPhaseType) {
        const studyListRequest = {
          panel_name: this.selectedPanelName,
          workflow: this.normalizeWorkflowForDisplay(this.selectedPhaseType),
          user_name: this.selectedUserName ?? undefined
        } as StudyListRequest;

        this.loadStudies(studyListRequest);
      }
    }
  }
  /** Handler for changes in the selected phase. */
  phaseChangeAction($event: MatSelectChange) {
    if ($event.value) {
      const studyListRequest = {
        panel_name: this.selectedPanelName,
        workflow: this.normalizeWorkflowForDisplay(this.selectedPhaseType),
        user_name: this.selectedUserName ?? undefined
      } as StudyListRequest;

      this.loadStudies(studyListRequest);
    }
  }
  /** Method to load the studies based on the selected panel and phase type. */
  loadStudies(studyListRequest: StudyListRequest) {
    if (studyListRequest) {
      this.isProcessing = true;
      this.errorMessage = '';

      const postObserver = {
        next: (studyList: StudyListResponse) => {
          console.log('studyList', studyList);
          this.selectedStudies = studyList.studies.slice().sort((a, b) => {
            if (!a.date && !b.date) return 0;
            if (!a.date) return 1;
            if (!b.date) return -1;
            return b.date.localeCompare(a.date);
          });
          this.selectedStudy = undefined;
          this.studyFilterText = '';
          this.applyStudyFilter();
        },
        error: (err: Error) => {

          this.errorMessage = this.appUtilityService.getError(err).message;
          this.isProcessing = false;
        },
        complete: () => {
          this.isProcessing = false;
        }
      } as Observer<StudyListResponse>;

      this.appUtilityService.post<StudyListRequest, StudyListResponse>(this.appUtilityService.getAPI(apiPaths.study.studyList), studyListRequest)
        .pipe(take(1))
        .subscribe(postObserver);
    }
  }

  applyStudyFilter() {
    const studies = this.selectedStudies ?? [];
    const filterText = this.studyFilterText.trim().toLowerCase();
    if (!filterText) {
      this.filteredStudies = studies;
      this.updateUserColWidth();
      return;
    }

    this.filteredStudies = studies.filter((study) => {
      const haystack = Object.values(study ?? {})
        .map(value => (value === null || value === undefined ? '' : String(value)))
        .join(' ')
        .toLowerCase();
      return haystack.includes(filterText);
    });
    this.updateUserColWidth();
  }

  private updateUserColWidth(): void {
    const longest = this.filteredStudies.reduce((max, s) => Math.max(max, (s.user ?? '').length), 0);
    this.userColWidth = longest > 0 ? `${longest + 1}ch` : '8rem';
  }


  load() {
    if (!this.selectedStudy) {
      return;
    }

    this.store.dispatch(setProcessingStatus({ isProcessing: true, activity: 'loading' }));
    this.store.dispatch(setLoadStudyError({ message: null }));
    this.errorMessage = '';

    const selectedStudy = this.selectedStudy[0];
    const studyLoadRequest = {
      run_name: selectedStudy.run_name,
    } as StudyLoadRequest;

    if (selectedStudy.is_draft) {
      const postObserver = {
        next: (draftResponse: LoadDraftStudyResponse) => {
          const draftStudy = this.buildStudyFromDraftData(draftResponse.draft_data);
          // Ensure drafts are treated as loaded studies by giving them a stable ID.
          draftStudy.studyId = selectedStudy.run_name;
          draftStudy.isDraft = true;
          draftStudy.auditTrail = draftResponse.audit_trail ?? [];
          this.hydrateDraftPanelSpecs(draftResponse.draft_data, draftStudy);
        },
        error: (err: Error) => {
          this.errorMessage = this.appUtilityService.getError(err).message;
          this.isProcessing = false;
          this.store.dispatch(setProcessingStatus({ isProcessing: false }));
          this.store.dispatch(setLoadStudyError({ message: 'Error loading study' }));
        },
        complete: () => {
          this.store.dispatch(setProcessingStatus({ isProcessing: false }));
        }
      } as Observer<LoadDraftStudyResponse>;

      this.appUtilityService.post<StudyLoadRequest, LoadDraftStudyResponse>(
        this.appUtilityService.getAPI(apiPaths.study.loadDraft),
        studyLoadRequest
      )
        .pipe(take(1))
        .subscribe(postObserver);
      return;
    }

    const postObserver = {
      next: (study: Study) => {
        study.isCommitted = !!study.isCommitted;
        study.isDraft = false;
        this.finalizeLoadedStudy(study);
      },
      error: (err: Error) => {
        this.errorMessage = this.appUtilityService.getError(err).message;
        this.isProcessing = false;
        this.store.dispatch(setProcessingStatus({ isProcessing: false }));
        this.store.dispatch(setLoadStudyError({ message: 'Error loading study' }));
      },
      complete: () => {
        this.store.dispatch(setProcessingStatus({ isProcessing: false }));
      }
    } as Observer<Study>;

    this.appUtilityService.post<StudyLoadRequest, Study>(this.appUtilityService.getAPI(apiPaths.study.load), studyLoadRequest)
      .pipe(take(1))
      .subscribe(postObserver);
  }

  private finalizeLoadedStudy(study: Study): void {
    study.studyId = study.customStudyId ?? '';
    const latestComment = getLatestAuditTrailComment(study.auditTrail);
    if (latestComment) {
      study.comments = latestComment;
    } else {
      study.comments = study.comments ?? '';
    }

    const reportContentItems = study.reportContentItems ?? {
      variableColumns: [],
      reportContentItems: []
    } as ReportContentItemResponse;
    study.reportContentItems = reportContentItems;

    this.normalizeCalculationIds(study);
    this.normalizeAssignSpec(study);
    this.normalizeReportContentRunIds(study);
    this.applyRunValueOptimizationFromCalcResponse(study);
    this.sortReportContentItems(study);
    this.alignRunSampleLots(study);
    // Normalize accession IDs to uppercase and deduplicate, keeping the last occurrence of each accession.
    const sampleByAccession = new Map<string, Sample>();
    (study.runSamples ?? []).forEach(s => {
      const key = s.accessionId?.trim().toUpperCase() ?? s.accessionId;
      sampleByAccession.set(key, { ...s, accessionId: key });
    });
    study.runSamples = Array.from(sampleByAccession.values());
    study.runSampleLots = applySampleTypeOverrides(
      study.runSampleLots,
      study.runSamples,
      study.reportContentItems?.reportContentItems ?? []
    );
    // The load response is camelCase while Study is snake_case, so this is mapped by hand rather
    // than relying on the names lining up. Decimal may arrive as a number or a string; null stays
    // null so the Adjusted Calibrator screen falls back to the admin default.
    const loaded = study as Study & { allowedAdjustmentFactor?: number | string | null; calAdjustmentMode?: CalAdjustmentMode | null };
    study.allowed_adjustment_factor =
      loaded.allowedAdjustmentFactor == null ? null : Number(loaded.allowedAdjustmentFactor);
    study.cal_adjustment_mode = loaded.calAdjustmentMode ?? null;
    if (isCalAdjustFinal(study)) {
      // A Final study has no baseline. One re-saved over an Adjust study can still hold the old
      // unadjusted half, so drop it rather than show it beside the Final results.
      study.calculationResponse = { calculationResponse: null, valueAssignment: 'OriginalCalculate' };
      study.unadjustedCalculationId = null;
    }
    this.store.dispatch(setStudy({ selectedStudy: study }));
    const assayNames = study.panelSpecs?.Assay_Specs.map(assay => assay.assay_name) ?? [];
    const beadRegions = study.panelSpecs?.Assay_Specs.map(assay => assay.bead_region) ?? [];
    const panelName = study.panelSpecs?.kitname ?? this.selectedPanelName ?? '';
    this.store.dispatch(setPanel({
      panelName: panelName,
      panelSpecs: study.panelSpecs,
      runType: study.runType,
      reportContentItems: study.reportContentItems,
      assayNames: assayNames,
      beadRegions: beadRegions,
      isRestore: true
      }));
  }

  private hydrateDraftPanelSpecs(draftData: DraftStudyData, study: Study): void {
    const panelName = draftData.run?.spec_kit_name ?? this.selectedPanelName;
    // The saved study keeps its own run type, but panel specs are stored per API workflow, so
    // the split match kit run types have to look their specs up as MatchKit.
    const workflowType = toApiWorkflow(draftData.run?.workflow);
    const revision = draftData.revisionNum ?? '';

    if (!panelName || !workflowType || !revision) {
      study.panelSpecs = null;
      this.finalizeLoadedStudy(study);
      this.enqueueDraftLotImports(study, revision);
      return;
    }

    const postObserver = {
      next: (panelResponse: Panel) => {
        study.panelSpecs = panelResponse;
        this.finalizeLoadedStudy(study);
        this.enqueueDraftLotImports(study, revision);
      },
      error: (err: Error) => {
        this.errorMessage = this.appUtilityService.getError(err).message;
        study.panelSpecs = null;
        this.finalizeLoadedStudy(study);
        this.enqueueDraftLotImports(study, revision);
      }
    } as Observer<Panel>;

    this.appUtilityService.get<Panel>(
      this.appUtilityService.getAPI(`${apiPaths.panelInfo.panelSpecs}${panelName}/${workflowType}/${revision}`)
    )
      .pipe(take(1))
      .subscribe(postObserver);
  }

  private buildStudyFromDraftData(draftData: DraftStudyData): Study {
    const study = new Study('');
    const runInfo = draftData.run ?? { name: '' };

    study.studyName = runInfo.name ?? '';
    study.runType = runInfo.workflow ?? '';
    study.testSpec = runInfo.test_spec ?? '';
    study.assignSpec = runInfo.formulation_stage ?? '';
    study.comments = draftData.run?.notes ?? draftData.notes ?? '';
    study.calculate_averages = runInfo.calculate_averages ?? false;
    study.use_legacy_4pl = runInfo.use_legacy_4pl ?? false;
    study.skip_missing_samples = runInfo.skip_missing_samples ?? false;
    // null, not 0: an unset override is what makes the Adjusted Calibrator screen
    // fall back to the calibration administration default.
    study.allowed_adjustment_factor = runInfo.allowed_adjustment_factor ?? null;
    study.cal_adjustment_mode = runInfo.cal_adjustment_mode ?? null;
    study.unadjustedCalculationId = draftData.unadjusted_calculation_id ?? null;
    study.adjustedCalculationId = draftData.adjusted_calculation_id ?? null;
    // Carried through the draft like any other saved field: a draft that dropped them would
    // reopen showing the panel's defaults, silently discarding what the operator certified.
    study.coaDetails = draftData.coa_details ?? null;
    study.reportContentItems = this.buildReportContentFromDraftData(draftData);
    study.runSamples = this.buildSamplesFromDraftData(draftData);
    study.runSampleLots = this.buildRunSampleLotsFromDraftData(draftData, study.runSamples);
    study.calLots = this.buildCalLotsFromDraftData(draftData);
    study.qcLots = this.buildQcLotsFromDraftData(draftData);

    const kitLots = Object.values(draftData.pack_lot_numbers ?? {});
    study.kitLots = Array.from(new Set(kitLots));
    study.refKitLot = this.resolveDraftReferenceLot(draftData);
    study.assayNames = Object.values(draftData.run_config?.assays ?? {});
    study.beadRegions = Object.values(draftData.run_config?.bead_regions ?? {}).map(value => Number(value));

    this.applyInstrumentFileInfoToStudy(study, draftData);

    study.calculationResponse = null;
    study.calculationAdjustedResponse = null;
    study.sampleDataOriginal = [];
    study.sampleDataAdjusted = [];
    study.sampleDataFailuresOriginal = [];
    study.sampleDataFailuresAdjusted = [];
    study.auditTrail = [];
    study.isCommitted = false;

    return study;
  }

  private enqueueDraftLotImports(study: Study, specRevision: string): void {
    if (!specRevision) {
      return;
    }

    const calLots = study.calLots ?? [];
    calLots.forEach(lot => {
      if (!lot?.calLotNumber) {
        return;
      }
      const hasDetails = (lot.assays?.length ?? 0) > 0 || (lot.vials?.length ?? 0) > 0;
      if (hasDetails) {
        return;
      }
      // The saved version, so the draft reopens on the values it was saved against.
      this.store.dispatch(loadCalLot({
        lotNumber: lot.calLotNumber,
        specRevision,
        assignmentVersion: lot.valueAssignmentVersion ?? null,
        isRestore: true
      }));
    });

    const qcLots = study.qcLots ?? [];
    qcLots.forEach(lot => {
      if (!lot?.masterLotNumber) {
        return;
      }
      const hasDetails = (lot.assays?.length ?? 0) > 0 || (lot.vials?.length ?? 0) > 0;
      if (hasDetails) {
        return;
      }
      this.store.dispatch(loadQcLot({
        lotNumber: lot.masterLotNumber,
        specRevision,
        assignmentVersion: lot.valueAssignmentVersion ?? null,
        isRestore: true
      }));
    });
  }

  private buildReportContentFromDraftData(draftData: DraftStudyData): ReportContentItemResponse {
    const reportContentItemIds = draftData.report_content_item_ids ?? [];
    const runConfig = draftData.run_config ?? {};
    const packLotNumbers = draftData.pack_lot_numbers ?? {};

    const items = reportContentItemIds.map((reportContentItemId) => {
      const item = new ReportContentItem();
      item.reportContentItemId = reportContentItemId;
      item.lot = packLotNumbers[reportContentItemId] ?? '';
      item.assay = runConfig.assays?.[reportContentItemId] ?? '';
      item.runId = String(runConfig.instrument_ids?.[reportContentItemId] ?? '');
      item.resultType = runConfig.result_type?.[reportContentItemId] ?? '';
      item.region = Number(runConfig.bead_regions?.[reportContentItemId] ?? 0);
      item.runValueOptimization = this.normalizeOptimizationValue(
        runConfig.run_value_optimization?.[reportContentItemId]
      );
      item.calLot = runConfig.cal_lots?.[reportContentItemId] ?? '';
      item.beadCouplingDate = runConfig.bead_coupling_date?.[reportContentItemId] ?? '';
      item.coupledBeadLn = runConfig.coupled_bead_ln?.[reportContentItemId] ?? '';
      item.dyedBeadLn = runConfig.dyed_bead_ln?.[reportContentItemId] ?? '';
      item.progenitorLn = runConfig.progenitor_ln?.[reportContentItemId] ?? '';
      item.antigenLn = runConfig.antigen_ln?.[reportContentItemId] ?? '';
      item.antigenLoadLn = runConfig.antigen_loading?.[reportContentItemId] ?? '';
      item.coupledBeadPn = runConfig.coupled_bead_pn?.[reportContentItemId] ?? '';
      item.ligandPn = runConfig.ligand_pn?.[reportContentItemId] ?? '';
      item.beadReagentPn = runConfig.bead_region_pn?.[reportContentItemId] ?? '';
      item.beadReagentLn = runConfig.bead_reagent_ln?.[reportContentItemId] ?? '';
      item.sampleDiluentLn = runConfig.sample_diluent_ln?.[reportContentItemId] ?? '';
      item.conjugateReagentLn = runConfig.conjugate_reagent_ln?.[reportContentItemId] ?? '';
      item.particleDiluentLn = runConfig.particle_diluent_ln?.[reportContentItemId] ?? '';
      item.conjugateDiluentLn = runConfig.conjugate_diluent_ln?.[reportContentItemId] ?? '';
      item.washBufferLn = runConfig.wash_buffer_ln?.[reportContentItemId] ?? '';
      item.sheathFluidLn = runConfig.sheath_fluid_ln?.[reportContentItemId] ?? '';
      item.description = runConfig.description?.[reportContentItemId] ?? '';
      item.units = '';
      item.resultDecimalPrecision = 0;
      item.userDefinedDecimalPrecision = 0;
      item.qcLot = '';
      return item;
    });

    return {
      variableColumns: [],
      reportContentItems: items
    } as ReportContentItemResponse;
  }

  private buildSamplesFromDraftData(draftData: DraftStudyData): Sample[] {
    const sampleInfo = draftData.sample_info;
    if (!sampleInfo) {
      return [];
    }

    const accessions = sampleInfo.accession_number ?? [];
    const sampleIds = sampleInfo.sample_id ?? [];
    const sampleTypes = sampleInfo.sample_type ?? [];
    const replicates = sampleInfo.num_replicates ?? [];

    return accessions.map((accessionId, index) => {
      return {
        accessionId: accessionId.trim().toUpperCase(),
        sampleId: sampleIds[index] ?? null,
        defaultSampleType: sampleTypes[index] ?? '',
        sampleType: sampleTypes[index] ?? '',
        reqNumReplicates: replicates[index] ?? 0
      } as Sample;
    });
  }

  // A draft saves each report item's overrides as a JSON array of sample types in sample order
  // ("[]" when the column has none). Without this the Run Samples tab reopens on the defaults.
  private buildRunSampleLotsFromDraftData(draftData: DraftStudyData, samples: Sample[]): RunSampleLot[] {
    const saved = (draftData.run_config?.sample_type_overrides ?? {}) as Record<string, string | string[]>;
    const lots: RunSampleLot[] = [];

    (draftData.report_content_item_ids ?? []).forEach(reportContentItemId => {
      let types: unknown = saved[reportContentItemId];
      if (typeof types === 'string') {
        try {
          types = JSON.parse(types);
        } catch {
          types = [];
        }
      }
      if (!Array.isArray(types) || !types.length) {
        return;
      }

      const lot = new RunSampleLot();
      lot.reportContentItemId = reportContentItemId as unknown as number;
      lot.meanType = '-';
      lot.runSamples = samples.map((sample, index) => ({
        sampleId: sample.sampleId as string,
        sampleType: (types as string[])[index] || sample.sampleType || sample.defaultSampleType
      }));
      lots.push(lot);
    });

    return lots;
  }

  // Deduped on the way in too, which repairs a study already saved with a lot listed twice.
  private buildCalLotsFromDraftData(draftData: DraftStudyData): CalLot[] {
    return dedupeLotVersions((draftData.cal_lot_nums ?? []).map(entry => this.normalizeDraftLotEntry(entry))).map(normalized => {
      const lot = new CalLot();
      lot.calLotNumber = normalized.lotNumber;
      if (normalized.version !== null) {
        lot.valueAssignmentVersion = normalized.version;
      }
      return lot;
    });
  }

  private buildQcLotsFromDraftData(draftData: DraftStudyData): QcLot[] {
    return dedupeLotVersions((draftData.ctrl_lot_nums ?? []).map(entry => this.normalizeDraftLotEntry(entry))).map(normalized => {
      const lot = new QcLot();
      lot.masterLotNumber = normalized.lotNumber;
      if (normalized.version !== null) {
        lot.valueAssignmentVersion = normalized.version;
      }
      return lot;
    });
  }

  private normalizeDraftLotEntry(entry: DraftLotEntry): { lotNumber: string; version: number | null } {
    if (typeof entry === 'string') {
      return { lotNumber: entry, version: null };
    }

    const lotNumber = String(entry?.lotNumber ?? '');
    const version = typeof entry?.version === 'number' && Number.isFinite(entry.version)
      ? entry.version
      : null;
    return { lotNumber, version };
  }

  private applyInstrumentFileInfoToStudy(study: Study, draftData: DraftStudyData): void {
    const fileInfo = draftData.instr_file_info;
    if (!fileInfo) {
      study.patientFileMetadata = [];
      study.calFileMetadata = [];
      study.controlFileMetadata = [];
      return;
    }

    const fileNames = fileInfo.file_names ?? [];
    const fileTypes = fileInfo.file_types ?? [];
    const instrumentIds = fileInfo.instrument_ids ?? [];
    const serialNumbers = fileInfo.serial_numbers ?? [];
    const softwareVersions = fileInfo.software_versions ?? [];

    const patientFiles: FileMetaData[] = [];
    const calFiles: FileMetaData[] = [];
    const controlFiles: FileMetaData[] = [];

    fileNames.forEach((fileName, index) => {
      const type = fileTypes[index] ?? '';
      const fileMeta = this.buildFileMetaData({
        fileName: fileName,
        type: type,
        instrumentId: instrumentIds[index] ?? '',
        serialNumber: serialNumbers[index] ?? '',
        softwareVersion: softwareVersions[index] ?? ''
      });

      switch (type) {
        case 'PatientResults':
          patientFiles.push(fileMeta);
          break;
        case 'CalibrationResults':
          calFiles.push(fileMeta);
          break;
        case 'ControlResults':
          controlFiles.push(fileMeta);
          break;
        default:
          patientFiles.push(fileMeta);
          break;
      }
    });

    study.patientFileMetadata = patientFiles;
    study.calFileMetadata = calFiles;
    study.controlFileMetadata = controlFiles;
  }

  private buildFileMetaData(data: {
    fileName: string;
    type: string;
    instrumentId: string;
    serialNumber: string;
    softwareVersion: string;
  }): FileMetaData {
    const base: FileMetaData = {
      runId: '',
      date: '',
      instrumentSerialNo: data.serialNumber,
      softwareVersion: data.softwareVersion,
      fileName: data.fileName,
      type: data.type,
      accessions: [],
      reagentLots: [],
      panels: [],
      instrumentId: data.instrumentId,
      fileValidity: null,
      replicateCounts: {}
    };

    const fileMeta = new FileMetaData(base);
    return fileMeta;
  }

  private normalizeAssignSpec(study: Study): void {
    if (study.assignSpec && study.assignSpec.trim().length) {
      return;
    }

    const formulationStage = (study as { formulationStage?: string | null }).formulationStage;
    if (!formulationStage) {
      return;
    }

    const normalized = formulationStage.trim();
    if (!normalized) {
      return;
    }

    study.assignSpec = normalized;
  }

  private normalizeWorkflowForDisplay(workflow: string | null | undefined): string {
    const normalized = (workflow ?? '').trim();
    if (!normalized) {
      return '';
    }
    if (normalized === 'Calibrator Adjustment') {
      return 'CalibratorAdjustment';
    }
    if (normalized === 'Control Assignment') {
      return 'ControlAssignment';
    }
    return normalized;
  }

  private normalizeReportContentRunIds(study: Study): void {
    this.dedupeReportContentItems(study);
    const reportContent = study.reportContentItems?.reportContentItems ?? [];
    if (!reportContent.length) {
      return;
    }

    const instrumentIds = new Set<string>();
    const metadataSources = [
      ...(study.patientFileMetadata ?? []),
      ...(study.calFileMetadata ?? []),
      ...(study.controlFileMetadata ?? [])
    ];
    metadataSources.forEach(file => {
      if (file?.instrumentId) {
        instrumentIds.add(String(file.instrumentId));
      }
    });
    if (!instrumentIds.size) {
      console.info('[LoadStudy] normalizeReportContentRunIds: no instrument IDs found', {
        reportContentCount: reportContent.length
      });
      return;
    }

    let matched = 0;
    let updated = 0;
    reportContent.forEach(item => {
      const currentStr = String(item.runId ?? '');
      item.runId = currentStr;
      if (instrumentIds.has(currentStr)) {
        matched += 1;
        return;
      }

      const reportId = item.reportContentItemId ?? '';
      const underscoreIndex = reportId.lastIndexOf('_');
      if (underscoreIndex === -1) {
        return;
      }

      const suffix = reportId.slice(underscoreIndex + 1);
      if (instrumentIds.has(suffix)) {
        item.runId = suffix;
        updated += 1;
      }
    });

    console.info('[LoadStudy] normalizeReportContentRunIds summary', {
      reportContentCount: reportContent.length,
      instrumentIds: Array.from(instrumentIds),
      matched,
      updated
    });
  }

  private normalizeCalculationIds(study: Study): void {
    const extendedStudy = study as Study & {
      unadjusted_calculation_id?: string | null;
      adjusted_calculation_id?: string | null;
    };
    study.unadjustedCalculationId =
      extendedStudy.unadjustedCalculationId
      ?? extendedStudy.unadjusted_calculation_id
      ?? null;
    study.adjustedCalculationId =
      extendedStudy.adjustedCalculationId
      ?? extendedStudy.adjusted_calculation_id
      ?? null;
  }

  /**
   * Restores each report item's optimization algorithm from the calculation response.
   *
   * The saved-study load response does not echo `run_value_optimization` onto the report content
   * items, so without this the Adjusted Calibrator dropdown reopens at N/A even for a study that
   * ran an optimal adjustment — and the next save would then persist that N/A. The calculation
   * response carries the method each item actually ran with, keyed by the same composite id the
   * backend builds report content item ids from (`<kitLot><beadRegion>_<instrumentId>`).
   *
   * The draft path sets the value from the draft's own run config before it gets here, so an item
   * that already carries a value is left alone.
   */
  private applyRunValueOptimizationFromCalcResponse(study: Study): void {
    const items = study.reportContentItems?.reportContentItems ?? [];
    if (!items.length) {
      return;
    }

    const validOptions = new Set(['N/A', 'powell', 'proportional_sweep']);
    const optimizationById = new Map<string, string>();
    // The adjusted response is the one that ran the optimization; the unadjusted response only
    // fills gaps (an unadjusted-only study reports N/A throughout, which is correct for it).
    const responses = [
      study.calculationResponse?.calculationResponse?.reportContentItems,
      study.calculationAdjustedResponse?.calculationResponse?.reportContentItems
    ];
    responses.forEach(reportItems => {
      (reportItems ?? []).forEach(reportItem => {
        const id = `${reportItem.kitLotNumber}${reportItem.BeadRegion}_${reportItem.instrumentID}`;
        optimizationById.set(id, this.normalizeOptimizationValue(reportItem.runValueOptimization));
      });
    });

    items.forEach(item => {
      if (typeof item.runValueOptimization === 'string' && validOptions.has(item.runValueOptimization)) {
        return;
      }
      item.runValueOptimization = optimizationById.get(item.reportContentItemId) ?? 'N/A';
    });
  }

  private normalizeOptimizationValue(value: string | boolean | undefined): string {
    if (typeof value !== 'string') {
      return 'N/A';
    }

    const normalized = value.trim().toLowerCase().replace(/\s+/g, '_');
    if (normalized === 'n/a' || normalized === 'na') {
      return 'N/A';
    }
    if (normalized === 'powell') {
      return 'powell';
    }
    if (normalized === 'proportional_sweep') {
      return 'proportional_sweep';
    }

    return 'N/A';
  }

  private resolveDraftReferenceLot(draftData: DraftStudyData): string {
    const referenceLots = Object.values(draftData.run_config?.reference_lots ?? {})
      .map(value => String(value ?? '').trim());
    const savedReferenceLot = referenceLots.find(value => value.length > 0);
    return savedReferenceLot ?? '';
  }

  deleteStudy() {
    if (!this.selectedStudy?.length) {
      return;
    }
    const studyName = this.selectedStudy[0].run_name;
    this.isProcessing = true;
    this.errorMessage = '';
    const deleteRequest = {
      run_name: studyName
    } as StudyLoadRequest;

    const postObserver = {
      next: () => {
        this.selectedStudies = (this.selectedStudies ?? []).filter(study => study.run_name !== studyName);
        this.applyStudyFilter();
        this.selectedStudy = undefined;
        // The deleted study, and any linkage naming it, would otherwise still be listed on the
        // Studies screen from what it fetched when it opened.
        this.studyLinksService.invalidate();
        this.studyLogbookService.invalidate();
      },
      error: (err: Error) => {
        this.errorMessage = this.appUtilityService.getError(err).message;
        this.isProcessing = false;
      },
      complete: () => {
        this.isProcessing = false;
      }
    } as Observer<unknown>;

    this.appUtilityService.post<StudyLoadRequest, unknown>(
      this.appUtilityService.getAPI(apiPaths.study.delete),
      deleteRequest
    )
      .pipe(take(1))
      .subscribe(postObserver);
  }

  private sortReportContentItems(study: Study): void {
    const reportContent = study.reportContentItems?.reportContentItems ?? [];
    if (!reportContent.length) {
      return;
    }

    reportContent.sort((left, right) => {
      const regionCompare = this.compareBeadRegions(left.region, right.region);
      if (regionCompare !== 0) {
        return regionCompare;
      }

      return (left.assay ?? '').localeCompare(right.assay ?? '');
    });
  }

  private alignRunSampleLots(study: Study): void {
    const reportContent = study.reportContentItems?.reportContentItems ?? [];
    if (!reportContent.length || !study.runSampleLots?.length) {
      return;
    }
    study.runSampleLots = alignRunSampleLots(study.runSampleLots, reportContent);
  }

  private toNumber(value: string | number | undefined): number {
    if (typeof value === 'number') {
      return Number.isNaN(value) ? 0 : value;
    }
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private compareBeadRegions(left: string | number | undefined, right: string | number | undefined): number {
    const leftRegion = this.toNumber(left);
    const rightRegion = this.toNumber(right);
    const leftInternal = this.internalQcBeadRegions.has(leftRegion);
    const rightInternal = this.internalQcBeadRegions.has(rightRegion);
    if (leftInternal !== rightInternal) {
      return leftInternal ? 1 : -1;
    }
    return leftRegion - rightRegion;
  }

  private dedupeReportContentItems(study: Study): void {
    const reportContent = study.reportContentItems?.reportContentItems ?? [];
    if (!reportContent.length) {
      return;
    }

    const merged = new Map<string, typeof reportContent[number]>();
    reportContent.forEach(item => {
      const id = item.reportContentItemId ?? '';
      if (!id) {
        return;
      }
      const existing = merged.get(id);
      if (!existing) {
        merged.set(id, item);
        return;
      }
      merged.set(id, this.mergeReportContentItems(existing, item));
    });

    study.reportContentItems = {
      ...(study.reportContentItems ?? { variableColumns: [], reportContentItems: [] }),
      reportContentItems: Array.from(merged.values())
    };
  }

  private mergeReportContentItems(
    existing: Study['reportContentItems']['reportContentItems'][number],
    incoming: Study['reportContentItems']['reportContentItems'][number]
  ): Study['reportContentItems']['reportContentItems'][number] {
    const merged: typeof existing = { ...existing };
    Object.keys(incoming).forEach(key => {
      const typedKey = key as keyof typeof incoming;
      const currentValue = merged[typedKey];
      const nextValue = incoming[typedKey];
      if (currentValue === null || currentValue === undefined || currentValue === '') {
        merged[typedKey] = nextValue;
      }
    });
    return merged;
  }
  /** Method to confirm the study load action. */
  confirmLoadStudy() {
    this.isLoadStudyConfirmed = true;
    this.dialogRef.updateSize('50vw');
  }
  /** ngOnDestroy lifecycle hook for cleanup. */
  ngOnDestroy() {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }

}